import { useQueries } from '@tanstack/react-query';
import { useMemo } from 'react';

// A field's options are usually just the first page of a fetch (e.g. limit: 100).
// If a previously-selected id (loaded from a saved/shared URL filter) isn't in
// that page, it silently fails to resolve to a name/avatar in the select UI.
// This fetches exactly those missing ids by id so selections always resolve,
// regardless of pagination or search narrowing.
export function useResolveMissingSelections<T>({
  clientId,
  selectedIds,
  availableIds,
  fetchOne,
  queryKeyPrefix,
  enabled = true,
}: {
  clientId: string;
  selectedIds: string[];
  availableIds: Set<string>;
  fetchOne: (clientId: string, id: string) => Promise<T>;
  queryKeyPrefix: string;
  enabled?: boolean;
}): T[] {
  const missingIds = useMemo(
    () => Array.from(new Set(selectedIds.filter(id => id && !availableIds.has(id)))),
    [selectedIds, availableIds]
  );

  const queries = useQueries({
    queries: missingIds.map(id => ({
      queryKey: [queryKeyPrefix, clientId, id],
      queryFn: () => fetchOne(clientId, id),
      enabled: enabled && !!clientId,
    })),
  });

  return useMemo(
    () => queries.map(query => query.data).filter((entity): entity is T => Boolean(entity)),
    [queries]
  );
}
